import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Stack, Title, Text, Anchor, Paper, List, Divider, Group, Badge, ThemeIcon, Tooltip, useComputedColorScheme,
} from '@mantine/core';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import config from '../../config';
import DraftBadge from '../common/DraftBadge';

// Single long static reference for the sales funnel / Vesper pipeline, deep-linkable via URL
// hash (#anchor). Each Section renders an element with `id={...}`; links elsewhere in the app
// (e.g. the decision-trace score drawer) point at /sales/funnel/documentation#priority-score
// and this page scrolls there on load. Written for business users — no code, plain language.
//
// The score weights are fetched live from GET /api/vesper/scoring-weights (VesperOptions), so the
// numbers here track the real config — change a weight and the docs update. DEFAULTS mirror the
// current config so the page renders instantly (and offline) before/if the fetch resolves.

const round1 = (v) => Math.round(Number(v) * 10) / 10;

const DEFAULTS = {
  fill: { haveAll: 40, haveNone: 5 },
  value: {
    cap: 25,
    basis: 'margin_dollars',
    bands: [{ min: 2500, points: 25 }, { min: 1000, points: 18 }, { min: 500, points: 12 }, { min: 200, points: 6 }, { min: 0.01, points: 2 }],
  },
  customer: { key: 20, operator: 12 },
  rating: { a: 4, b: 2 },
  freshness: { cap: 10, tauDays: 2 },
  targetPlatforms: [
    { platform: '737NG', tokens: ['737NG'] },
    { platform: '737CL', tokens: ['737CL'] },
    { platform: '777',   tokens: ['777'] },
    { platform: 'A320',  tokens: ['A320', 'A320VIN', 'A320ENH', 'A320NEO'] },
  ],
  nudgeCap: 10, scoreMax: 100,
  derived: { practicalMax: 99, haveNoneMax: 39 },
};

// Mirrors GET /api/vesper/pricing-weights so the pricing section renders instantly before the fetch.
const PRICING_DEFAULTS = {
  targetMargin: 0.30,   // one target margin for everyone — see the Target margin section
  dosLadder: [
    { minDays: 0, maxDays: 60, multiplier: 1.00, discountPct: 0 },
    { minDays: 61, maxDays: 120, multiplier: 0.95, discountPct: 5 },
    { minDays: 121, maxDays: 180, multiplier: 0.90, discountPct: 10 },
    { minDays: 181, maxDays: 270, multiplier: 0.88, discountPct: 12 },
    { minDays: 271, maxDays: null, multiplier: 0.85, discountPct: 15 },
  ],
  expiry: { classA: 90, others: 180 },
  compWindowYears: 3,
};

// A section wrapper whose id is the anchor target. scrollMarginTop keeps the heading clear of any
// fixed chrome when we scroll to it.
function Section({ id, title, children }) {
  return (
    <div id={id} style={{ scrollMarginTop: 90 }}>
      <Title order={2} mt={40} mb="sm">{title}</Title>
      {children}
    </div>
  );
}

// One row of the decision-trace legend. The colours are copied from DecisionCanvas's TYPE_BD so the
// documentation and the diagram cannot drift apart — if you change one, change the other.
function LegendRow({ color, glyph, name, what }) {
  return (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      <div style={{
        flex: '0 0 auto', width: 78, height: 26, borderRadius: 6,
        border: `2px solid ${color}`, background: `${color}1a`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {glyph && <Text size="xs" style={{ color }}>{glyph}</Text>}
      </div>
      <div>
        <Text size="sm" fw={600}>{name}</Text>
        <Text size="xs" c="dimmed">{what}</Text>
      </div>
    </Group>
  );
}

const TOC = [
  { id: 'who', label: 'The sales enrichment pipeline and the AI Sales Agent — what are they?' },
  { id: 'decision-trace', label: 'The decision trace (the diagram)' },
  { id: 'overview', label: 'How opportunities are scored & handled' },
  { id: 'data', label: 'Step 1 · The data inputs' },
  { id: 'priority-score', label: 'Step 2 · The priority score' },
  { id: 'fillable-gate', label: '— Can we fill it? (the gate)' },
  { id: 'score-factors', label: '— The four factors & their weights' },
  { id: 'coverage-buckets', label: '— In-stock coverage' },
  { id: 'line-value', label: '— Line value (what separates a deal’s lines)' },
  { id: 'margin', label: 'Est. gross margin — what a sale costs us' },
  { id: 'margin-basecost', label: '— The airframe base cost (BCR)' },
  { id: 'margin-repair', label: '— Repair cost accrues onto the part' },
  { id: 'cost-basis', label: '— Cost basis: BCR / no BCR / consignment' },
  { id: 'margin-fifo', label: '— Which unit we price (the oldest)' },
  { id: 'margin-estimate', label: '— Why it says “Est.”' },
  { id: 'platform-fit', label: 'Platform fit (our target aircraft)' },
  { id: 'customer-priority', label: '— Customer priority & rating' },
  { id: 'freshness', label: '— RFQ freshness' },
  { id: 'capping-nudge', label: '— Capping & the judge nudge' },
  { id: 'reading-the-drawer', label: '— Reading the score drawer' },
  { id: 'pricing', label: 'Step 3 · How pricing works' },
  { id: 'pricing-not-price', label: '— Signals, not the final price' },
  { id: 'pricing-scenarios', label: '— Good / expired / no pricing' },
  { id: 'pricing-ssp-dos', label: '— Governed price: SSP × days-on-shelf' },
  { id: 'pricing-basis', label: '— Which price we anchor on' },
  { id: 'pricing-min-sell', label: '— Minimum sell price (the one hard rule)' },
  { id: 'pricing-economic-limit', label: '— The economic limit' },
  { id: 'pricing-margins', label: '— The target margin' },
  { id: 'pricing-drawer', label: '— Reading the pricing drawer' },
  { id: 'summarize', label: 'Step 4 · How summarization works' },
  { id: 'summarize-what', label: '— What it produces' },
  { id: 'summarize-rules', label: '— The rules it follows' },
  { id: 'summarize-drawer', label: '— Reading the summary drawer' },
  { id: 'guidance', label: 'Guidance (house & advisor)' },
  { id: 'judge', label: 'Step 5 · How judgment works' },
  { id: 'judge-what', label: '— The bounded nudge & the decision' },
  { id: 'judge-actions', label: '— The recommended actions' },
  { id: 'judge-escalation', label: '— Escalation & review' },
  { id: 'judge-drawer', label: '— Reading the judgment drawer' },
  { id: 'deal-summary', label: 'Step 6 · The deal summary' },
  { id: 'nymeria', label: 'The recommendation (AI Sales Agent)' },
  { id: 'nymeria-decision', label: '— The decision: quote, hold, or pass' },
  { id: 'nymeria-gate', label: '— When it runs (the gate)' },
  { id: 'nymeria-drawer', label: '— Reading the recommendation drawer' },
];

export default function FunnelDocumentation() {
  const location = useLocation();
  const { data } = useAuthenticatedData(`${config.apiBaseUrl}/api/vesper/scoring-weights`);
  const { data: pdata } = useAuthenticatedData(`${config.apiBaseUrl}/api/vesper/pricing-weights`);

  // Merge live config over the defaults (shallow-per-group) so a partial/absent response is safe.
  const d = data || {};
  const w = {
    fill:      { ...DEFAULTS.fill, ...(d.fill || {}) },
    // `value` (the per-line margin band) must be listed here like every other group — this merge is
    // enumerated, not a spread, so a group that is absent from it is undefined no matter what the API sends.
    value:     { ...DEFAULTS.value, ...(d.value || {}) },
    customer:  { ...DEFAULTS.customer, ...(d.customer || {}) },
    rating:    { ...DEFAULTS.rating, ...(d.rating || {}) },
    freshness: { ...DEFAULTS.freshness, ...(d.freshness || {}) },
    targetPlatforms: d.targetPlatforms ?? DEFAULTS.targetPlatforms,
    nudgeCap:  d.nudgeCap ?? DEFAULTS.nudgeCap,
    scoreMax:  d.scoreMax ?? DEFAULTS.scoreMax,
    derived:   { ...DEFAULTS.derived, ...(d.derived || {}) },
  };

  const pd = pdata || {};
  const p = {
    targetMargin:    pd.targetMargin ?? PRICING_DEFAULTS.targetMargin,
    dosLadder:       Array.isArray(pd.dosLadder) && pd.dosLadder.length ? pd.dosLadder : PRICING_DEFAULTS.dosLadder,
    expiry:          { ...PRICING_DEFAULTS.expiry, ...(pd.expiry || {}) },
    compWindowYears: pd.compWindowYears ?? PRICING_DEFAULTS.compWindowYears,
  };

  // Scroll to the anchor named in the URL hash, on load and whenever it changes. The timeout lets
  // the content render first (the target element must exist before scrollIntoView can find it).
  useEffect(() => {
    const id = (location.hash || '').replace('#', '');
    if (!id) { window.scrollTo({ top: 0 }); return; }
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    return () => clearTimeout(t);
  }, [location.hash]);

  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <Stack gap="md" p="md" style={{ maxWidth: 900 }}>
      <div>
        <DraftBadge />
        <Title order={1}>Sales Funnel — Documentation</Title>
        <Text size="sm" c="dimmed">
          How the <b>sales enrichment pipeline</b> reads each opportunity, scores it and prices it, and how the{' '}
          <b>AI Sales Agent</b> decides what to quote. Written for business users — no code, plain language. Start with{' '}
          <b>who they are</b> below if these two are new to you.
        </Text>
      </div>

      <Paper withBorder radius="md" p="md">
        <Text fw={600} size="sm" mb={6}>Contents</Text>
        <Stack gap={2}>
          {TOC.map((t) => (
            <Anchor key={t.id} href={`#${t.id}`} size="sm"
              onClick={(e) => { e.preventDefault(); window.history.replaceState(null, '', `#${t.id}`); jump(t.id); }}
              style={{ paddingLeft: t.label.startsWith('—') ? 16 : 0 }}>
              {t.label}
            </Anchor>
          ))}
        </Stack>
      </Paper>

      {/* ── Who ── The two names this document uses constantly and never defined. */}
      <Section id="who" title="The sales enrichment pipeline and the AI Sales Agent — what are they?">
        <Text size="sm">
          Two things come up on nearly every screen, and they are not the same. The short version:
          <b> The sales enrichment pipeline reads and prepares. The AI Sales Agent decides.</b>
        </Text>

        <Paper withBorder radius="md" p="md" mt="sm">
          <Group gap="xs" mb={4}>
            <ThemeIcon size="sm" radius="sm" color="gray"><span style={{ fontSize: 12 }}>⚙</span></ThemeIcon>
            <Text fw={700} size="sm">The sales enrichment pipeline</Text>
          </Group>
          <Text size="sm">
            The sales enrichment pipeline is the <b>automatic pipeline</b> that runs the moment an RFQ arrives,
            before anyone opens it. It gathers the facts (the part, our stock, the customer, what we have sold and
            quoted), scores the line, works out the pricing signals, and judges whether the line is worth quoting.
            You do not trigger it and you cannot talk to it — you read what it produced.
          </Text>
          <Text size="sm" mt={6}>
            Most of the pipeline is <b>deterministic</b>: plain, repeatable business rules that give the same answer
            for the same facts, every time. Two of its steps use AI, and they are marked as such wherever they
            appear.
          </Text>
        </Paper>

        <Paper withBorder radius="md" p="md" mt="sm">
          <Group gap="xs" mb={4}>
            <ThemeIcon size="sm" radius="sm" color="grape"><span style={{ fontSize: 12 }}>✦</span></ThemeIcon>
            <Text fw={700} size="sm">The AI Sales Agent</Text>
          </Group>
          <Text size="sm">
            The AI Sales Agent is an <b>AI agent</b> that makes the actual call: quote this line, at this price,
            or hold, or pass. It runs <i>after</i> the pipeline, sees everything the pipeline assembled plus
            leadership&rsquo;s guidance, and produces the recommendation you read at the top of the cockpit.
          </Text>
          <Text size="sm" mt={6}>
            The AI Sales Agent <b>recommends; it does not act.</b> It cannot send a quote, change a price, or
            touch a customer. A person does that. It is also the thing you can <b>talk to</b> — the chat rail is
            the agent.
          </Text>
        </Paper>

        <Text size="sm" mt="sm">
          So when a screen says a number is &ldquo;deterministic&rdquo;, it came from the pipeline&rsquo;s rules and
          you can reproduce it by hand. When it says <b>AI Sales Agent</b>, it is a judgment — reasoned from the same
          facts you can see, and open to disagreement.
        </Text>
      </Section>

      {/* ── The decision trace ── The diagram everything else refers to but never introduced. */}
      <Section id="decision-trace" title="The decision trace (the diagram)">
        <Text size="sm">
          Everything below can be <b>seen</b>, per deal, on a diagram called the <b>decision trace</b>. It draws
          every step that was taken, what fed it, and what it produced — so you never have to take a score or a
          price on trust.
        </Text>

        <Text fw={600} size="sm" mt="md">Where to find it</Text>
        <Text size="sm">
          Open a deal in the <b>Quoting Cockpit</b> and click the small <b>graph icon</b> (
          <Text span style={{ fontFamily: 'monospace' }}>⛶</Text>) in the corner of a panel. It appears in three
          places, and <i>which one you click decides what you see</i>:
        </Text>
        <List size="sm" mt={6} spacing={4}>
          <List.Item>
            the icon on the <b>deal summary</b> at the top opens <b>“The whole deal”</b> — the fan-out: the
            customer brief, then one box per line, then the deal summary. Click any line to drop into it.
          </List.Item>
          <List.Item>
            the icons on <b>the AI Sales Agent&rsquo;s recommendation</b> and on <b>Signals &amp; judgment</b> open
            <b> “This line”</b> — the full chain for the one line you are looking at.
          </List.Item>
        </List>
        <Text size="sm" mt={6}>
          The two tabs are not a preference: they are two different <b>grains</b>. A line&rsquo;s trace shows
          only that line — never another part on the same RFQ, never a deal-wide total — because a price and a
          verdict belong to one part, and mixing in a sibling&rsquo;s facts is how a good line gets judged on a
          bad one&rsquo;s.
        </Text>

        <Text fw={600} size="sm" mt="md">What the boxes mean</Text>
        <Text size="sm" mb={6}>
          Every box carries a <b>colour</b> and a small <b>glyph</b> in its top-right corner. Together they tell
          you <i>what kind of thing</i> the box is — not how important it is. The glyph is the quickest way in:
          spot it on the diagram, find the same glyph below. This is the legend printed above the diagram
          itself, so the two always agree.
        </Text>
        <Paper withBorder radius="md" p="md">
          <Stack gap={8}>
            <LegendRow color="#4dabf7" glyph="▤" name="Inputs (data)"
              what="The raw facts, straight from our systems: the customer record, what we hold in stock, recent sales and quotes, who else wants the part. These are things we LOOKED UP, not things we worked out. Click one to see the exact rows used." />
            <LegendRow color="#868e96" glyph="⚙" name="Deterministic stage"
              what="A step with plain, repeatable rules — the score, the pricing signals. Same facts in, same answer out, every time. No AI, nothing to argue with." />
            <LegendRow color="#7048e8" glyph="✳" name="AI stage"
              what="A step where a language model read the facts and wrote something: the customer brief, the line summary, the judgment. These cost money to run and can be wrong, so they are always marked." />
            <LegendRow color="#ae3ec9" glyph="✦" name="AI Sales Agent"
              what="The AI Sales Agent's recommendation — the final call on whether and how to quote." />
            <LegendRow color="#f06595" glyph="§" name="Guidance"
              what="Leadership's written rules that were applied to this line — house rules, the customer's segment, rules for this part. These are policy, not data." />
            <LegendRow color="#40c057" glyph="▸" name="Output → next stage"
              what="An internal handoff: something one step produced purely to feed the next. You rarely need these." />
            <LegendRow color="#f76707" glyph="◉" name="Shown to rep"
              what="A result that ends up on YOUR screen — the priority, the line summary, the price, the recommendation. If you want to know where a number on the cockpit came from, find the box with this glyph." />
          </Stack>
        </Paper>

        <Text size="sm" mt="md">
          The <b>deal-level</b> diagram (“The whole deal”) uses two more:
        </Text>
        <Paper withBorder radius="md" p="md" mt={6}>
          <Stack gap={8}>
            <LegendRow color="#0ca678" glyph="◆" name="Line"
              what="One requested line of the RFQ, collapsed to its part, condition and verdict. Click it to open that line's own trace." />
            <LegendRow color="#ced4da" glyph="ⓘ" name="Note"
              what="An explanation, not a step — e.g. why a single-line RFQ has no deal summary." />
          </Stack>
        </Paper>

        <Text size="sm" mt="md">
          <b>Click any box.</b> A drawer opens with exactly what went into that step and what came out — the
          real rows, the real numbers, at the moment the decision was made. Nothing is recomputed for the
          drawer, so what you see is what was actually used.
        </Text>
        <Text size="sm" mt={6}>
          The <b>arrows are the argument</b>. A box with no arrow into the judgment did not influence the
          verdict, however interesting it looks — the line summary is the clearest example: it is written for
          you, and the judge never reads it.
        </Text>
      </Section>

      {/* ── Overview ── */}
      <Section id="overview" title="How opportunities are scored & handled">
        <Text size="sm">
          Every incoming RFQ (request for quote) runs through a short pipeline. The early steps are
          <b> deterministic</b> — plain, repeatable business rules that always produce the same answer
          for the same facts. Later steps use AI to summarize and judge, and the AI Sales Agent makes the final
          quoting recommendation.
        </Text>
        <Text size="sm" mt="sm">
          <b>The shape matters more than the order.</b> An RFQ with three lines is not handled as one thing —
          it fans out:
        </Text>
        <Text size="xs" ff="monospace" mt={6} style={{ whiteSpace: 'pre', lineHeight: 1.5 }}>
{`                     ┌── line 1: summary → judge → agent   ──┐
  Customer brief ─────┼── line 2:      "                "     ──┼──► Deal summary
   (once per          └── line 3:      "                "     ──┘   (multi-line RFQs
    customer)               each line judged ON ITS OWN            only, right at the end)`}
        </Text>
        <Text size="sm" mt={6}>
          Each line is scored, summarized and judged <b>knowing nothing about the other lines</b>. That is
          deliberate: if the judge could see that line 2 is unfillable, it would start reasoning about line 1
          comparatively — and one part's verdict must not depend on what else happened to be on the same email.
        </Text>
        <List size="sm" mt="sm" spacing={4}>
          <List.Item><b>Customer brief</b> — a short AI brief on the <b>customer</b>: who they are, their health, any credit hold. Written once and shown against every line, because it is about the account, not the enquiry — it never mentions a part.</List.Item>
          <List.Item><b>Step 1 · Context</b> — gathers the facts: the part, our inventory, the customer, recent sales & quotes, who else wants the part.</List.Item>
          <List.Item><b>Step 2 · Score</b> — the deterministic priority score.</List.Item>
          <List.Item><b>Step 3 · Pricing</b> — the deterministic price signals: the anchor, any minimum sell price on the unit, and the economic limit.</List.Item>
          <List.Item><b>Step 4 · Line summary</b> — a short AI summary of <b>this one line</b>: what they want, whether we can fill it, and the thing that decides how to treat it. Written for you, not for the pipeline — the judge is deliberately not shown it.</List.Item>
          <List.Item><b>Step 5 · Judgement</b> — an AI review of <b>each line on its own</b>: it can nudge that line's score within a strict limit, and decides whether the line is worth quoting.</List.Item>
          <List.Item><b>AI Sales Agent</b> — the final recommendation on whether and how to quote — <b>per line</b>.</List.Item>
          <List.Item><b>Deal summary</b> — on multi-line RFQs only, and <b>last</b>: one AI synthesis of the per-line verdicts, after the AI Sales Agent has priced them. A single-line RFQ doesn't need it — that line's own verdict already says it all.</List.Item>
        </List>
        <Text size="xs" c="dimmed" mt={6}>
          Steps 1–5 are the <b>per-line</b> part, and the decision trace on a line shows exactly those. The
          customer brief and the deal summary are the two steps that are <i>not</i> per line — one runs before
          the fan-out, one after the fan-in — so they appear in the deal-level trace instead, under
          “The whole deal”.
        </Text>
      </Section>

      <Divider my="sm" />

      {/* ── Data inputs ── */}
      <Section id="data" title="Step 1 · The data inputs">
        <Text size="sm">
          The <b>blue blocks</b> feeding the Context step are the raw facts the pipeline reasons over — the
          "evidence package." The Context step gathers them <b>deterministically</b> from our own systems (the ERP,
          the data warehouse, and the RFQ feed) — no AI, no guesswork.
        </Text>
        <Text size="sm" mt="xs">Collectively they cover, for each requested part:</Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item>the <b>part</b> and its list prices;</List.Item>
          <List.Item>what we <b>hold in stock</b> and what it cost us (scrap and quarantined units excluded — they are not sellable);</List.Item>
          <List.Item>the <b>customer</b> and their history with us;</List.Item>
          <List.Item>recent <b>sales, quotes, and repairs</b> for the part — sales here means <b>realized</b> sales (shipped, revenue-contributing), not orders that may never have shipped;</List.Item>
          <List.Item><b>who else wants the part</b> right now (open demand elsewhere in the company);</List.Item>
          <List.Item>
            the <b>pricing probes</b> — our market research on this part. See below.
          </List.Item>
        </List>

        <Text size="sm" fw={600} mt="md">Pricing probes: market pricing and repair averages</Text>
        <Text size="sm" mt={4}>
          ITS records market research through Pentagon&apos;s RFQ feature, because that timestamps it cleanly.
          They are not really requests for quote — they are <b>pricing probes</b>, and there are two kinds:
        </Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item>
            <b>Market pricing</b> — what our <b>competitors sell the part for</b>. This is a{' '}
            <b>comparable</b>, in the same family as our own sales history: it tells you what the part fetches
            in the market, and whether there is headroom above our price. It is <b>not our cost</b> and it is{' '}
            <b>never a price floor</b> — competitor asking prices run about <b>1.4×</b> our own realized sell
            price, so treating them as a cost would argue every correctly-priced quote upward. The pricing
            engine deliberately leaves them <b>out</b> of the price ladder; they inform the judgment, they do
            not move the number.
          </List.Item>
          <List.Item>
            <b>Repair averages</b> — what it <b>should cost to repair</b> the part. This one genuinely is a
            cost, and it does move the number: it is what the engine&apos;s repair-implied limit is built from
            (an AR core should clear at least what a serviceable unit sells for, less the repair).
          </List.Item>
        </List>
        <Text size="sm" mt="xs" c="dimmed">
          Vocabulary, because it is easy to mix up: a <b>quote</b> at ITS is always <b>outbound</b> — us
          quoting a customer. A <b>PO</b> is how we actually buy parts, and it is not part of this pipeline.
        </Text>

        <Text size="sm" mt="md">
          Every history feed is bounded to the <b>last 3 years</b> — one window, shared by every step. When
          the windows drifted apart they produced real disagreements: the judge once scored quote history over
          3 years while the AI Sales Agent read <i>all time</i>, so the step that <i>gates</i> the recommendation and the
          recommendation itself were reasoning over different pasts.
        </Text>
        <Text size="sm" mt="xs">
          Everything downstream — the score, the summary, the judge, the pricing, and the AI Sales Agent — reads from
          this <b>same package</b>, and so do the panels on your cockpit screen. That is deliberate: the AI
          and the rep must be looking at the same world, or a recommendation you cannot verify against what is
          in front of you is worse than no recommendation at all. Click any blue block in the trace to see
          exactly what it held for that deal.
        </Text>
      </Section>

      <Divider my="sm" />

      {/* ── Priority score ── */}
      <Section id="priority-score" title="Step 2 · The priority score">
        <Text size="sm">
          The priority score is a number from <b>0 to {w.scoreMax}</b> that ranks the opportunity in
          your worklist — higher means "look at this sooner." It's built from four simple factors that
          are <b>added together</b> and then capped to the 0–{w.scoreMax} range. Because it's a fixed
          formula, the same facts always give the same score.
        </Text>
        <Text size="sm" mt="xs">
          On a multi-line RFQ, <b>each line is scored on its own</b> — and the opportunity's score is the
          <b> highest line's</b> score. A junk line we can't fill doesn't drag the opportunity down: if we
          can fully fill our best line, the opportunity scores as that line. (The customer, rating, and
          freshness factors are the same for every line; only the in-stock coverage differs per line.)
        </Text>
      </Section>

      <Section id="fillable-gate" title="Can we fill it? (the gate)">
        <Text size="sm">
          The single biggest driver is whether we can actually supply the part. An opportunity we can
          fill from stock is worth far more of our attention than one we can't. This shows up two ways:
          a <b>Can fill / Cannot fill</b> badge, and the largest points contribution coming from
          in-stock coverage.
        </Text>
      </Section>

      <Section id="score-factors" title="The four factors & their weights">
        <Text size="sm">
          <b>Every line is scored on its own.</b> The opportunity's score is simply the highest-scoring
          line — a line we can't fill never drags down one we can. The score adds up these factors.
          <b> In-stock coverage</b> is the gate and <b>line value</b> is what separates one line of a deal
          from another; customer, rating and freshness are properties of the <i>deal</i>, so they are the
          same for every line on it.
        </Text>
        <ScoreWeightsFormula w={w} />
        <Text size="xs" c="dimmed" mt={10}>
          These are the current live weights (pulled from the scoring config, so this updates if the
          tuning changes). By design a not-in-stock line tops out at <b>{w.derived.haveNoneMax}</b> points
          ({w.fill.haveNone} + {w.customer.key} + {w.rating.a} + {w.freshness.cap} — it earns no value
          points, because a line we can't fill has no margin), which stays below the <b>{w.fill.haveAll}</b> that
          any fillable line starts at. So "can we supply it?" always wins.
        </Text>
      </Section>

      <Section id="coverage-buckets" title="In-stock coverage — the gate">
        <Text size="sm">
          Coverage is decided <b>per line</b>, on that line's own requested condition and quantity:
        </Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item><b>In stock</b> → <b>{w.fill.haveAll}</b> points — we hold the requested condition. Clears the gate.</List.Item>
          <List.Item><b>Not in stock</b> → <b>{w.fill.haveNone}</b> points — not held in the requested condition, and the "Cannot fill" badge.</List.Item>
        </List>
        <Text size="sm" mt="xs">
          Coverage is a <b>gate, not the whole score</b>. It answers "can we supply this?" — it says nothing
          about whether the line is worth chasing. That is what <a href="#line-value">line value</a> is for.
          A line only counts as in stock if we hold the <i>condition the customer asked for</i>: AR stock does
          not fill an SV request (though the cockpit shows you the AR so you can offer it deliberately).
        </Text>
      </Section>

      <Section id="line-value" title="Line value — what separates the lines of a deal">
        <Text size="sm">
          Customer, rating and freshness are all deal-level, so they are identical for every line on a deal.
          If coverage were the only per-line factor, <b>every fillable line of a deal would score exactly the
          same</b> — a $3,000-margin line would be indistinguishable from a $500 one. Line value fixes that:
          each line earns points for the margin we estimate we'd make on it (unit margin × quantity).
        </Text>
        <List size="sm" mt="xs" spacing={4}>
          {(w.value?.bands || []).map((b, i) => (
            <List.Item key={i}>
              <b>${Number(b.min).toLocaleString()}+</b> estimated margin → <b>{b.points}</b> points
            </List.Item>
          ))}
        </List>
        <Text size="sm" mt="xs">
          Banded rather than a straight line, so one unusually large line can't swamp the scale, and so the
          steps stay legible. A line we <b>cannot fill has no margin estimate</b>, so it earns <b>0</b> here —
          which is what keeps the fill gate intact.
        </Text>
      </Section>

      <Section id="margin" title="Est. gross margin — what a sale actually costs us">
        <Text size="sm">
          Margin at ITS is <b>not</b> "sell price minus what we paid". We buy <b>whole aircraft on teardown</b>,
          and the parts come off the airframe at <b>$0 book cost</b> — <b>98% of everything on our shelves is
          booked at zero</b>. So "what we paid" is almost never a usable number, and a naive margin would say
          every AR part is 100% profit.
        </Text>
        <Text size="sm" mt="xs">
          What a sale really costs us is decided by <b>which airframe the part came off</b>, and <b>whether that
          airframe has been paid for yet</b>.
        </Text>
      </Section>

      <Section id="margin-basecost" title="The airframe base cost (and how it gets repaid)">
        <Text size="sm">
          When we buy an aircraft, its purchase price is recorded as a <b>base cost</b> against the warehouse
          that its parts land in. That warehouse is effectively "the airframe". Every part sold out of it
          <b> pays back a share of the sale price</b> — typically <b>70–80%</b> — toward that base cost, until
          the aircraft is paid off.
        </Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item>
            While base cost remains, that warehouse is flagged <b>BCR</b> — <i>Base Cost Remaining</i>.
          </List.Item>
          <List.Item>
            Once it's paid off, it becomes <b>no&nbsp;BCR</b>: nothing is clawed back any more, and the margin on
            those parts jumps.
          </List.Item>
        </List>
        <Text size="sm" mt="xs">
          This is why <b>a low margin on BCR stock is not a bad deal</b>. The money isn't lost — it is
          <b> retiring the aircraft loan</b>, and while base cost remains we are <b>paying interest on it</b>.
          Selling BCR stock stops that clock. Enjoy the fat margins on paid-off stock, but don't let the BCR
          timer run.
        </Text>
      </Section>

      <Section id="margin-repair" title="Repair cost accrues onto the part">
        <Text size="sm">
          A part that comes off an airframe as-removed (AR) has had nothing spent on it. Send it out for repair
          and <b>the repair cost is added to that unit's cost</b>. That's why cost climbs as a part is worked up
          the condition ladder — and why an OH unit is genuinely more expensive to us than the AR core it
          started as:
        </Text>
        <List size="sm" mt="xs" spacing={2}>
          <List.Item><b>AR</b> — 99% are booked at $0 (straight off the airframe).</List.Item>
          <List.Item><b>SV</b> — average ~$2,100 when a cost has accrued.</List.Item>
          <List.Item><b>OH</b> — average ~$4,100.</List.Item>
        </List>
        <Text size="sm" mt="xs">
          When the sale happens, we <b>recover that repair cost first</b>, and only then does the airframe take
          its share of what's left.
        </Text>
      </Section>

      <Section id="cost-basis" title="Cost basis — why two identical parts have different margins">
        <Text size="sm">
          Every line in the cockpit shows a <b>cost basis</b> badge next to its margin. It is the single most
          important thing to read, because the margin number is meaningless without it:
        </Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item>
            <b>no BCR</b> — that airframe is <b>paid off</b>. Nothing is clawed back, so the sale is nearly all
            gross margin. Historically these clear <b>~100%</b> on AR.
          </List.Item>
          <List.Item>
            <b>BCR</b> — <b>still being repaid</b>, so 70–80% of the sale goes back to the airframe. Realized
            margin is <b>~20–25%</b> — but the sale retires the debt and stops the interest.
          </List.Item>
          <List.Item>
            <b>consignment</b> — the stock is <b>owned by the vendor</b>, not us. We can sell it, and we do, but
            the margin is their split (<b>~18–23%</b>). It was never our teardown margin.
          </List.Item>
        </List>
        <Text size="sm" mt="md">
          So the <i>same part, same condition, same $0 book cost</i> is worth <b>~100%</b> gross out of a paid-off
          airframe and <b>~25%</b> out of one still being repaid. <b>Four times the difference</b>, decided
          entirely by which warehouse the unit happens to sit in.
        </Text>

        <Paper withBorder radius="md" p="sm" mt="md">
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={6}>Worked example</Text>
          <Text size="sm">
            A serviceable part sells for <b>$6,000</b>. It came off an airframe still being repaid (BCR, 75%
            payback), and we spent <b>$2,000</b> repairing it:
          </Text>
          <List size="sm" mt={6} spacing={2}>
            <List.Item>We recover the <b>$2,000</b> repair first.</List.Item>
            <List.Item>Of the <b>$4,000</b> left, the airframe takes <b>75% = $3,000</b> toward its base cost.</List.Item>
            <List.Item>Cost of the sale = $2,000 + $3,000 = <b>$5,000</b> → <b>Est. gross margin $1,000 (17%)</b>.</List.Item>
          </List>
          <Text size="sm" mt={6}>
            Had that same part come from a <b>paid-off</b> airframe, the cost would be just the <b>$2,000</b>
            repair — <b>$4,000 margin (67%)</b>.
          </Text>
        </Paper>
      </Section>

      <Section id="margin-fifo" title="Which unit do we price? The oldest one">
        <Text size="sm">
          When we hold several units that match a request, they are usually <b>not all from the same
          airframe</b> — and since the airframe decides the margin, we have to say which unit we mean. We use
          <b> FIFO: the oldest unit ships</b>. Its cost, its cost basis and its days-on-shelf are what you see.
        </Text>
        <Text size="sm" mt="xs">
          The cockpit says so next to the margin — <i>"oldest unit of 5 · 4,459d on shelf"</i> — and names the
          exact unit in the badge tooltip.
        </Text>
        <Text size="sm" mt="xs">
          This is not a technicality. On about <b>1 in 5</b> stocked lines the matching units span more than one
          cost basis, so choosing a different unit would report a <b>materially different margin for the very
          same part</b>. Shipping oldest-first is also simply good practice: it moves aged stock, and it lines up
          with the <a href="#pricing-ssp-dos">days-on-shelf discount</a> used in pricing.
        </Text>
      </Section>

      <Section id="margin-estimate" title="Why it says “Est.” gross margin">
        <Text size="sm">
          We call it <b>Est. gross margin</b> — never just "margin" — and that word is doing real work. The
          calculation reproduces the cost our ERP actually books on a sale <b>about 93% of the time</b>. The rest
          comes down to manual cost adjustments and timing differences we can't see in advance.
        </Text>
        <Text size="sm" mt="xs">
          It is a <b>decision signal</b> — good enough to rank which line is worth chasing and to warn you when a
          sale is thin — but it is <b>not an accounting figure</b>. Don't quote it to Finance.
        </Text>
      </Section>

      <Section id="platform-fit" title="Platform fit — our target aircraft">
        <Text size="sm">
          ITS goes after parts and customers for a handful of <b>target platforms</b>. Operators of these are
          less price-sensitive and carry better margin, so a part or a customer that lines up with them is worth
          more to us. The cockpit now <b>computes</b> that fit instead of leaving you (or the AI) to eyeball two
          lists.
        </Text>

        <Text size="sm" fw={600} mt="md">Our target platforms</Text>
        <Group gap={8} mt={6}>
          {(w.targetPlatforms || []).map((t) => (
            <Tooltip key={t.platform} multiline w={240} withArrow
              label={t.tokens.length > 1
                ? `Also matches ${t.tokens.filter((x) => x !== t.platform).join(', ')} — the parts catalogue records the variant, customers just say ${t.platform}.`
                : `Matched exactly as "${t.platform}".`}>
              <Badge size="lg" color="blue" variant="light" style={{ cursor: 'help' }}>{t.platform}</Badge>
            </Tooltip>
          ))}
        </Group>
        <Text size="xs" c="dimmed" mt={6}>
          The A320 badge also covers <b>A320VIN / A320ENH / A320NEO</b> — the parts catalogue records the exact
          build while customers just record "A320". They are the same aircraft. (A319/A321 are <i>not</i>
          included — different types.)
        </Text>

        <Text size="sm" fw={600} mt="md">Two kinds of fit</Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item>
            <b>Platform fit (this line)</b> — does <i>this part</i> fit one of our target platforms?
            <b> Strong</b> = it is mainly a part for that platform · <b>medium</b> = it also fits one, among
            others · <b>weak</b> = we know its platforms and none is ours · <b>unknown</b> = no platform data.
          </List.Item>
          <List.Item>
            <b>Platform fit (customer)</b> — does <i>this customer</i> fly our target platforms?
            <b> Strong</b> = two or more · <b>medium</b> = one · <b>weak</b> = we know their fleet and none is
            ours · <b>unknown</b> = no fleet on record.
          </List.Item>
        </List>

        <Paper withBorder radius="md" p="sm" mt="md" style={{ borderLeft: '4px solid var(--mantine-color-gray-5)' }}>
          <Text size="sm" fw={700}>"Unknown" does not mean "bad"</Text>
          <Text size="sm">
            Most of the accounts that send us RFQs are <b>brokers and distributors — they do not fly aircraft at
            all</b>, so there is no fleet to record. That is <b>unknown</b>, not a poor fit. The genuine negative
            is <b>weak</b>: we <i>do</i> know their fleet, and none of it is ours.
          </Text>
        </Paper>

        <Text size="xs" c="dimmed" mt={10}>
          Platform fit is shown to you, and given to the judge and to the AI Sales Agent as a fact — it does not currently
          move the priority score.
        </Text>
      </Section>

      <Section id="customer-priority" title="Customer priority & rating">
        <Text size="sm">Not all buyers carry equal weight. The score adds a bonus for:</Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item><b>Key customers</b> → <b>+{w.customer.key}</b> — designated strategic accounts.</List.Item>
          <List.Item><b>Airlines / operators</b> → <b>+{w.customer.operator}</b> — end users who fly the aircraft.</List.Item>
          <List.Item><b>Brokers / distributors</b> → <b>+0</b> — resellers, base weight.</List.Item>
        </List>
        <Text size="sm" mt="xs">
          Separately, account rating adds a small tiebreak: <b>A-rated → +{w.rating.a}</b>,
          <b> B-rated → +{w.rating.b}</b>. If the account is <b>on hold</b>, the drawer flags it — a
          signal to review before quoting.
        </Text>
      </Section>

      <Section id="freshness" title="RFQ freshness">
        <Text size="sm">
          A brand-new RFQ deserves faster attention than one that's been sitting for weeks. Freshness
          starts at <b>+{w.freshness.cap}</b> on day one and <b>decays smoothly with age</b> — it roughly
          drops to a third about every {w.freshness.tauDays} days, so a same-day request gets the full
          bonus while an old one contributes almost nothing.
        </Text>
        <Paper withBorder radius="md" p="sm" mt="sm" style={{ display: 'inline-block' }}>
          <FreshnessCurve w={w} />
        </Paper>
      </Section>

      <Section id="capping-nudge" title="Capping & the judge nudge">
        <Text size="sm">
          After the four factors are added, the total is <b>capped to the 0–{w.scoreMax} range</b>
          (a sum above {w.scoreMax} becomes {w.scoreMax}; below 0 becomes 0) and rounded to a whole
          number. That capped number is the <b>base priority score</b> from the Score step. In practice the
          highest a line reaches is about <b>{w.derived.practicalMax}</b> ({w.fill.haveAll} +
          {w.value.cap} + {w.customer.key} + {w.rating.a} + {w.freshness.cap}) — a big-margin line, in
          stock, for a key A-rated account, on a brand-new RFQ.
        </Text>
        <Text size="sm" mt="xs">
          The judge can then apply a <b>bounded ±{w.nudgeCap} adjustment</b> — never more —
          based on cited guidance, to produce the final <b>Priority</b> the rep sees. The judge can
          refine the ranking, but it cannot overrule the deterministic base by a wide margin. This keeps
          the score explainable and stable while still allowing judgment at the edges.
        </Text>
      </Section>

      <Section id="reading-the-drawer" title="Reading the score drawer">
        <Text size="sm">
          In the decision trace (open a deal in the Quoting Cockpit → <b>Decision trace</b>), click the
          <b> Score</b> box. The drawer shows:
        </Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item>The score out of {w.scoreMax} and the Can fill / Cannot fill badge.</List.Item>
          <List.Item>Each of the four factors with a plain-English reason, a bar, and its points.</List.Item>
          <List.Item>The base score (with any rounding or capping noted).</List.Item>
          <List.Item>The hand-off to the judge and the ±{w.nudgeCap} limit.</List.Item>
        </List>
        <Group mt="md" gap="xs">
          <ThemeIcon variant="light" color="gray" size="sm" radius="xl">i</ThemeIcon>
          <Text size="xs" c="dimmed">
            The drawer reads the exact numbers captured when the deal was scored, so it always matches
            what the pipeline actually did.
          </Text>
        </Group>
      </Section>

      <Divider my="sm" />

      {/* ── Pricing ── */}
      <Section id="pricing" title="Step 3 · How pricing works">
        <Text size="sm">
          Right after scoring, the pricing step works out the <b>pricing signals</b> for each requested
          part: what our list price implies, what similar units recently sold for, what the repair
          economics say, whether the unit itself carries a <b>minimum sell price</b>, and the
          <b> economic limit</b> below which the deal stops making money. It's
          deterministic and reads only the facts gathered in the Context step (our inventory & cost,
          recent sales, the part's list prices), so it runs here with the other deterministic steps —
          the signals are ready and waiting when the AI Sales Agent makes the final call.
        </Text>
      </Section>

      <Section id="pricing-not-price" title="Signals, not the final price">
        <Paper withBorder radius="md" p="sm" style={{ borderLeft: '4px solid #7048e8' }}>
          <Text size="sm" fw={600}>The pricing step does not set the price.</Text>
          <Text size="sm" mt={4}>
            It produces a <b>starting anchor</b> and the limits around it — the guardrails. The actual quoted
            price is the <b>AI Sales Agent's</b> decision, made with judgment and the pricing guidance. Think of
            the pricing step as "here's the math and the line you can't cross," and the AI Sales Agent as "here's what we
            should actually quote this customer."
          </Text>
          <Text size="sm" mt={4}>
            The one exception is a <b>minimum sell price</b> set on the unit itself. That is not a signal — it
            is a decision someone already made, and neither the pipeline nor the AI Sales Agent may quote under it.
          </Text>
        </Paper>
      </Section>

      <Section id="pricing-scenarios" title="Good pricing, expired pricing, no pricing">
        <Text size="sm">
          Every part lands in one of three situations, depending on whether we have a <b>current</b>
          list price (SSP — System Sell Price) for the requested condition. List prices go stale:
          A-class parts expire after <b>{p.expiry.classA} days</b>, others after <b>{p.expiry.others} days</b>
          (and a price with no date is treated as expired).
        </Text>
        <Stack gap={8} mt="sm">
          <ScenarioRow n={1} color="#2f9e44" title="Good pricing"
            desc="We have an unexpired list price (SSP) for the requested condition — the clearest case. The governed SSP × days-on-shelf price becomes the anchor." />
          <ScenarioRow n={2} color="#e8590c" title="Expired pricing"
            desc="We have a list price, but it is out of date. It can't govern the price, so we fall back to recent sales, repair economics, or cost. Verify before quoting." />
          <ScenarioRow n={3} color="#e03131" title="No pricing"
            desc="No list price at all for that condition. We rely on a recent sale or cost; if there is nothing, the rep and the AI Sales Agent price it by judgment." />
        </Stack>
      </Section>

      <Section id="pricing-ssp-dos" title="Governed price: SSP × days-on-shelf">
        <Text size="sm">
          When the list price is current, we discount it based on how long the unit has physically sat
          in our warehouse — its <b>days on shelf (DOS)</b>. Older stock is discounted more, to keep
          inventory moving. The governed ladder:
        </Text>
        <DosLadderVisual p={p} />
        <Text size="xs" c="dimmed" mt={8}>
          Example: a unit that's been on the shelf 200 days falls in the 181–270 band — a
          {' '}{(p.dosLadder.find((b) => b.minDays <= 200 && (b.maxDays == null || b.maxDays >= 200)) || {}).discountPct}% discount
          off the list price. These ladder values are the live pricing config.
        </Text>
      </Section>

      <Section id="pricing-basis" title="Which price we anchor on">
        <Text size="sm">
          The pricing step picks the <b>anchor</b> — the reference price the rep and the AI Sales Agent start from — by
          walking down this list and taking the <b>first one available</b>:
        </Text>
        <PricingBasisFlow />
        <Text size="xs" c="dimmed" mt={8}>
          The anchor is a starting point, not the final quote. A unit's <b>minimum sell price</b>, where one
          exists, jumps this whole list — see below. Comparable sales and repair-implied limits look back
          <b> {p.compWindowYears} years</b>.
        </Text>
      </Section>

      <Section id="pricing-min-sell" title="Minimum sell price — when it exists, it IS the price">
        <Paper withBorder radius="md" p="sm" style={{ borderLeft: '4px solid #e03131' }}>
          <Text size="sm" fw={700}>This is the only hard price rule in the pipeline.</Text>
          <Text size="sm" mt={4}>
            A <b>minimum sell price</b> can be set on an <b>individual unit of stock</b> — a specific serial
            number on a specific shelf — rather than on the part. When one exists, <b>that is the price.</b>
          </Text>
        </Paper>
        <List size="sm" mt="sm" spacing={6}>
          <List.Item>
            <b>It overrides the list price (SSP)</b> — including when it is <i>lower</i>, which is the usual
            case. The SSP is a generic number for the part and is often years old; the minimum sell price is a
            considered number for the one item we would actually ship. We do not quote the stale generic price
            over the specific current one.
          </List.Item>
          <List.Item>
            <b>It is never discounted.</b> The days-on-shelf ladder does not touch it. Whoever priced that unit
            could already see how long it had been sitting there — knocking a further 15% off for age would be
            deducting twice for the same thing.
          </List.Item>
          <List.Item>
            <b>It never expires.</b> Unlike the list price, it does not go stale. If it is set, it is valid.
          </List.Item>
        </List>
        <Text size="sm" mt="sm">
          It is <b>rare</b> — well under 1% of the units we hold — but the units that carry one tend to be the
          expensive ones. Where it applies, the decision trace shows it as the chosen price, names the
          <b> serial number</b> it belongs to, and shows the list price beside it so you can see both and which
          one is governing.
        </Text>
        <Text size="sm" mt="sm">
          If the customer will not pay it, the answer is <b>&ldquo;no&rdquo;, not &ldquo;less&rdquo;</b>.
        </Text>
      </Section>

      <Section id="pricing-economic-limit" title="The economic limit (below this, we lose)">
        <Text size="sm">
          Where there is no minimum sell price, the pipeline still works out an <b>economic limit</b> — the
          point below which the deal stops making money for us. It computes up to two, and takes the
          <b> higher</b>:
        </Text>
        <List size="sm" mt="xs" spacing={6}>
          <List.Item>
            <b>Cost + margin</b> — our unit cost marked up to the target margin. If a unit costs $100 and the
            target margin is 30%, the limit is $100 ÷ (1 − 0.30) ≈ $143.
          </List.Item>
          <List.Item>
            <b>Repair-implied</b> (for as-removed / AR units) — a recent serviceable or overhauled sale price
            minus the average repair cost. It captures what the core is worth once repaired.
          </List.Item>
        </List>
        <Paper withBorder radius="md" p="sm" mt="sm" style={{ borderLeft: '4px solid #868e96' }}>
          <Text size="sm" fw={600}>Why it is not called &ldquo;the floor&rdquo;</Text>
          <Text size="sm" mt={4}>
            Because around here, <b>&ldquo;the floor&rdquo; means the SSP</b> — that is what people say and what
            they picture. Using the same word for this cost-derived number meant a screen saying &ldquo;never go
            below the floor&rdquo; pointed at one figure for the software and a different one for the person
            reading it. So this is the <b>economic limit</b>, and the word &ldquo;floor&rdquo; is not used for
            anything in the pricing step.
          </Text>
        </Paper>
        <Text size="sm" mt="sm">
          The economic limit is a <b>signal, not a rule</b>. The AI Sales Agent weighs it. The one thing it cannot do is
          raise a minimum sell price: we do not overrule a person&rsquo;s valuation of a unit to protect a
          margin target — if the two genuinely conflict, the line is flagged for a human instead.
        </Text>
      </Section>

      <Section id="pricing-margins" title="The target margin">
        <Text size="sm">
          Where we have a real cost for a unit, the cost limit is that cost plus a minimum target margin of
          <b> {Math.round(p.targetMargin * 100)}%</b> — so a $100 unit will not be quoted below
          <b> ${Math.round(100 / (1 - p.targetMargin))}</b>.
        </Text>
        <Text size="sm" mt="xs">
          It is the <b>same margin for every customer</b>. There used to be a ladder that gave "key" accounts a
          thinner limit and "low" accounts a fatter one — but that tier was calculated from an account's
          <i> annual revenue target</i>, i.e. what someone hoped they would buy, not what they actually buy. Who
          the customer is is not a pricing rule, so it no longer moves the price.
        </Text>
        <Text size="xs" c="dimmed" mt={8}>
          In practice this limit rarely binds: teardown stock is booked at $0, so most lines have no cost to
          build one from. See <a href="#margin">Est. gross margin</a>.
        </Text>
      </Section>

      <Section id="pricing-drawer" title="Reading the pricing drawer">
        <Text size="sm">
          In the decision trace, click the <b>Pricing</b> box. The drawer shows, per
          part: the scenario, the basis waterfall with the chosen anchor highlighted, any minimum sell price on
          the unit (with its serial), the binding never-below,
          and the hand-off to the AI Sales Agent for the final price decision.
        </Text>
        <Group mt="md" gap="xs">
          <ThemeIcon variant="light" color="gray" size="sm" radius="xl">i</ThemeIcon>
          <Text size="xs" c="dimmed">
            As with the score, the drawer shows the exact numbers captured when the deal was priced.
          </Text>
        </Group>
      </Section>

      <Divider my="sm" />

      {/* ── Summarize ── */}
      <Section id="summarize" title="Step 4 · How summarization works">
        <Text size="sm">
          Summarize is the pipeline's <b>first AI step</b>. It reads everything the Context step gathered (the part,
          our inventory, the customer, recent sales, who else wants the part) together with the Score,
          and writes a short, plain-English <b>brief</b> a rep can read in seconds. That same brief
          also becomes the input the judge reads. It <b>summarizes — it does not decide anything</b>.
        </Text>
        <Text size="sm" mt="xs">
          It uses a <b>fast, lightweight AI model</b> (the same tier as the judge, but with no escalation)
          — one quick pass, kept cheap because it runs on every opportunity.
        </Text>
      </Section>

      <Section id="summarize-what" title="What it produces">
        <SummarizeFlow />
        <Text size="sm" mt="md">
          The two summaries look like this in the cockpit (the <b>Customer</b> and <b>Opportunity</b>
          blocks) — short, specific, and grounded in the real numbers:
        </Text>
        <ExampleSummaries />
        <Text size="sm" mt="md">
          Plus a few <b>flags</b> — short, actionable callouts for anything that genuinely needs attention:
        </Text>
        <Group gap={6} mt={6}>
          {['Customer on hold', 'AOG priority', 'Multi-line RFQ', 'Also open: 2 quotes elsewhere'].map((f) => (
            <Badge key={f} color="orange" variant="light">{f}</Badge>
          ))}
        </Group>
      </Section>

      <Section id="summarize-rules" title="The rules it follows">
        <Text size="sm">The model is held to strict rules so the brief stays trustworthy:</Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item><b>Factual, with real numbers</b> — it cites the actual figures (e.g. "$450K annual value", "last order 14 days ago"), not vague claims.</List.Item>
          <List.Item><b>Never invents data</b> — if something is missing, it says so briefly instead of guessing.</List.Item>
          <List.Item><b>Short</b> — each summary is kept to roughly 60 words or less.</List.Item>
          <List.Item><b>Flags only what matters</b> — a handful of short, actionable flags, not noise.</List.Item>
          <List.Item><b>Calls out who else wants the part</b> — open demand elsewhere in the company (our open quotes + other inbound RFQs).</List.Item>
          <List.Item><b>Notes platform fit</b> — when the customer flies a target platform (737NG/737CL/777/A320) and the part fits, it flags the relationship-and-margin opportunity.</List.Item>
        </List>
      </Section>

      <Section id="summarize-drawer" title="Reading the summary drawer">
        <Text size="sm">
          In the decision trace, click the <b>Summarize</b> box. The drawer shows the AI model
          used, the <b>Customer</b> and <b>Opportunity</b> summaries, and the <b>flags</b> — the same brief
          that carries forward to the judge.
        </Text>
        <Group mt="md" gap="xs">
          <ThemeIcon variant="light" color="gray" size="sm" radius="xl">i</ThemeIcon>
          <Text size="xs" c="dimmed">
            As with the score and pricing, the drawer shows the exact text captured when the deal was summarized.
          </Text>
        </Group>
      </Section>

      <Divider my="sm" />

      {/* ── Guidance ── */}
      <Section id="guidance" title="Guidance (house & advisor)">
        <Text size="sm">
          Guidance is the written <b>playbook</b> — rules and posture authored by ITS leadership — that the
          pipeline injects into the AI <b>judge</b> and into the <b>AI Sales Agent</b>. The judge must apply
          the guidance (not its own opinions) and <b>cite the specific rules</b> it used. Certain
          non-negotiable rules are enforced automatically in code, never left to the AI.
        </Text>
        <Text size="sm" mt="xs">
          The <b>pink § blocks</b> in the trace are the guidance that applied to a given deal. The main tiers:
        </Text>
        <List size="sm" mt="xs" spacing={6}>
          <List.Item>
            <b>House</b> — the house rules and philosophy that apply to <b>everyone</b> (e.g. respond to
            fillable, in-stock RFQs from known accounts; prefer operators over brokers). House also carries
            the non-negotiable <b>hard rules</b> — credit-hold, do-not-quote, margin-floor — which are
            enforced in code, not left to the AI's discretion.
          </List.Item>
          <List.Item>
            <b>Advisor</b> — global rules for <b>how advice is voiced</b> to the rep: the tone and framing
            of the recommendation headline the AI Sales Agent writes. It shapes how we communicate, not whether to quote.
          </List.Item>
        </List>
        <Text size="sm" mt="xs">
          Other tiers are matched when relevant: <b>segment</b> (the posture for the customer's market
          segment), <b>customer</b> (a note on the specific account), <b>part</b> (by ATA chapter / class),
          and the <b>AI Sales Agent</b> policy (how the agent itself should behave).
        </Text>
        <Text size="sm" mt="xs">
          <b>Precedence:</b> narrower guidance overrides broader — <b>customer &gt; segment &gt; house</b> —
          but never past a house hard rule. Click a guidance block to see the exact rules that were in force.
        </Text>
      </Section>

      <Divider my="sm" />

      {/* ── Judge ── */}
      <Section id="judge" title="Step 5 · How judgment works">
        <Text size="sm">
          Judgement is the pipeline's <b>judge</b> — the second AI step. Its job is <b>not to re-score</b>.
          It reads the deterministic base score and its factor breakdown, the full context, the summary
          brief, and the <b>guidance written by ITS leadership</b>, then applies the judgment the numbers
          can't see: a <b>small, bounded, cited</b> adjustment, a treatment stance, a recommended action,
          and any flags. Every non-zero nudge must cite a specific guidance rule — the judge applies
          policy, not opinion.
        </Text>
      </Section>

      <Section id="judge-what" title="The bounded nudge & the decision">
        <Text size="sm">
          The deterministic score is the <b>anchor</b>; the judge only nuances it — by no more than
          <b> ±{w.nudgeCap} points</b>. It can refine the ranking, but it cannot overrule the base by a
          wide margin. That keeps priorities explainable while still allowing judgment at the edges.
        </Text>
        <JudgeNudge w={w} />
        <Text size="sm" mt="md">Alongside the nudge, the judge returns:</Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item><b>Treatment stance</b> — prioritize · standard · cautious · decline.</List.Item>
          <List.Item><b>Recommended action</b> — what to do next (see below).</List.Item>
          <List.Item><b>Guidance applied</b> — the exact leadership rules it cited for the decision.</List.Item>
          <List.Item><b>Reasoning</b> — a few terse steps showing how it got there.</List.Item>
          <List.Item><b>Flags</b> — short callouts (e.g. credit hold, thin margin, broker/reseller).</List.Item>
        </List>
      </Section>

      <Section id="judge-actions" title="The recommended actions">
        <Text size="sm">The judge picks one recommended action for the opportunity:</Text>
        <ActionCards />
        <Text size="sm" mt="sm">
          This action is also the <b>gate for the AI Sales Agent</b>: the AI Sales Agent drafts a quoting recommendation only
          when the action is <b>Quote now</b> or <b>Quote with review</b>. Weaker actions stop here.
        </Text>
      </Section>

      <Section id="judge-escalation" title="Escalation & review">
        <Text size="sm">
          The judge normally runs on a <b>fast model</b>, but automatically <b>escalates to a more
          capable model</b> when the stakes justify it — a key customer we can actually fill or that
          carries real margin, a high-margin deal, or a risk signal (customer on hold, negative margin,
          or a hard rule in force).
        </Text>
        <Text size="sm" mt="xs">
          It can also raise <b>“needs review”</b> (adjudication) — a signal for a human second look — when
          a hard rule applies, the customer is on credit hold, margin is negative, the situation is
          unusual, or its judgment genuinely conflicts with the base score on a high-stakes deal.
        </Text>
        <Text size="sm" mt="xs">
          <b>Hard rules</b> are absolute and enforced on top of the judge: <b>do-not-quote</b> forces a
          decline, <b>credit hold</b> blocks auto-quoting and routes to credit review, and a
          <b> margin-floor</b> breach routes to pricing review.
        </Text>
      </Section>

      <Section id="judge-drawer" title="Reading the judgment drawer">
        <Text size="sm">
          In the decision trace, click the <b>Judgement</b> box (or the amber <b>Priority</b>
          output). The drawer shows the model used, the nudge, the stance and recommended action, the
          cited guidance, the reasoning steps, any flags, and whether it was flagged for review.
        </Text>
        <Group mt="md" gap="xs">
          <ThemeIcon variant="light" color="gray" size="sm" radius="xl">i</ThemeIcon>
          <Text size="xs" c="dimmed">
            As with the other stages, the drawer shows the exact decision captured when the deal was judged.
          </Text>
        </Group>
      </Section>

      <Divider my="sm" />

      {/* ── Deal summary ── */}
      <Section id="deal-summary" title="Step 6 · The deal summary">
        <Text size="sm">
          Because every line is judged <b>on its own</b>, a multi-line RFQ ends up with several separate
          verdicts — and nothing yet answers the question the rep actually has: <b>"what do I do with this
          opportunity as a whole?"</b> Step 6 answers it. It reads the per-line verdicts and writes one
          decisive line: <i>"Quote 2 of 6 to Delta — the SV actuator and the OH valve; the other 4 we can't
          fill."</i> plus a short why and a couple of supporting facts.
        </Text>
        <Text size="sm" mt="xs">
          It <b>synthesizes — it never re-judges</b>. If a line was deprioritized, the summary won't tell you
          to quote it. And it only runs on <b>multi-line</b> deals: a single-line RFQ already has its answer
          in that line's own judgement and the AI Sales Agent's recommendation, so we don't pay for a second opinion.
        </Text>
        <Text size="sm" mt="xs">
          It's the panel at the <b>top of the cockpit</b>. The per-line detail lives just below it — the
          requested-lines table shows each line's own action, and the AI Sales Agent's recommendation is for the line
          you have selected.
        </Text>
      </Section>

      <Divider my="sm" />

      {/* ── Nymeria recommendation ── */}
      <Section id="nymeria" title="The recommendation (AI Sales Agent)">
        <Text size="sm">
          The <b>AI Sales Agent</b> is ITS's AI <b>sales agent</b> — the final decision-maker. Where the earlier stages
          perceive, score, and price, the AI Sales Agent <b>decides</b>. It sees the whole board — the customer, what we
          hold, the part's sales/quote/repair history, who else wants it, and our cost to source or repair —
          plus leadership's guidance, and makes the single call the rep should act on, <b>on top of the pipeline's
          deterministic never-below</b>.
        </Text>
        <NymeriaFlow />
      </Section>

      <Section id="nymeria-decision" title="The decision: quote, hold, or pass">
        <OutcomeCards />
        <Text size="sm" mt="md">Alongside the decision, it gives:</Text>
        <List size="sm" mt="xs" spacing={4}>
          <List.Item><b>The decision headline</b> — the concrete call in one line (part, condition, qty, price).</List.Item>
          <List.Item><b>The angle</b> — one sentence on the why: how to win it, or the trap to avoid.</List.Item>
          <List.Item><b>What drove it</b> — the specific facts behind the call (each naming a number or a rule).</List.Item>
          <List.Item><b>Guidance applied</b> — the leadership rules it cited.</List.Item>
        </List>
        <Text size="sm" mt="xs">
          <b>Guardrails:</b> any price it names is <b>at or above the pipeline's never-below</b> — and exactly a unit's minimum sell price where one is set;
          a hard rule (credit-hold, do-not-quote, margin-floor) is absolute; and every claim must trace to
          the data on the board.
        </Text>
      </Section>

      <Section id="nymeria-gate" title="When it runs (the gate)">
        <Text size="sm">
          An AI Sales Agent recommendation is a paid model call, so it runs <b>only when the judge warrants it</b> — when
          the judge's recommended action is <b>Quote now</b> or <b>Quote with review</b>. On weaker deals
          (deprioritize, hold-for-sourcing, request-more-info) it's <b>skipped</b> — no model call — and shown
          as "not warranted." This keeps spend on the deals that matter.
        </Text>
        <Text size="sm" mt="xs">
          The gate is an <b>admin setting</b> (Funnel → AI Sales Agent settings) and can be turned off to run a
          recommendation on every deal.
        </Text>
      </Section>

      <Section id="nymeria-drawer" title="Reading the recommendation drawer">
        <Text size="sm">
          In the decision trace, click the <b>AI Sales Agent · Recommendation</b> box (or its amber output). The
          drawer shows the model used, the decision, the angle, the key considerations, the reasoning steps,
          and the cited guidance — or, when the deal didn't warrant a recommendation, why it was skipped.
        </Text>
        <Group mt="md" gap="xs">
          <ThemeIcon variant="light" color="gray" size="sm" radius="xl">i</ThemeIcon>
          <Text size="xs" c="dimmed">
            The recommendation is cached per deal + part, so the drawer shows the exact call captured at run time.
          </Text>
        </Group>
      </Section>

      <Divider my="lg" />
      <Group justify="space-between">
        <Anchor size="sm" href="#overview" onClick={(e) => { e.preventDefault(); window.history.replaceState(null, '', '#overview'); jump('overview'); }}>
          ↑ Back to top
        </Anchor>
        <Badge variant="light" color="gray">More sections coming</Badge>
      </Group>
    </Stack>
  );
}

/* ─────────────────────────  visual: the weights formula  ───────────────────────── */

// tiny stroke icons (currentColor → tinted to the factor color by the wrapper)
const Ic = ({ children }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcBox = () => <Ic><path d="M3 7l9-4 9 4-9 4-9-4z" /><path d="M3 7v10l9 4 9-4V7" /><path d="M12 11v10" /></Ic>;
const IcUser = () => <Ic><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></Ic>;
const IcStar = () => <Ic><path d="M12 3l2.6 6.3 6.4.5-4.9 4.1 1.5 6.6L12 17.7 6.4 20.6l1.5-6.6L3 9.8l6.4-.5z" /></Ic>;
const IcClock = () => <Ic><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Ic>;

// A large dimmed operator glyph (+ or =) between the factor cards.
function Op({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
      fontWeight: 700, color: 'var(--mantine-color-dimmed)', padding: '0 2px', alignSelf: 'stretch' }}>
      {children}
    </div>
  );
}

function FactorCard({ f, dark }) {
  const pill = (pts, plus) => (
    <span style={{ fontWeight: 700, fontSize: 13, color: f.color, background: `${f.color}22`,
      borderRadius: 6, padding: '1px 8px', whiteSpace: 'nowrap' }}>
      {plus && Number(pts) > 0 ? '+' : ''}{round1(pts)}
    </span>
  );
  return (
    <div style={{ flex: '1 1 158px', minWidth: 158, maxWidth: 220, borderRadius: 10,
      border: `1px solid ${f.color}`, borderTop: `4px solid ${f.color}`, padding: '10px 12px',
      background: dark ? 'var(--mantine-color-dark-6)' : '#fff',
      display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Group gap={8} wrap="nowrap" align="center">
        <span style={{ color: f.color, display: 'flex' }}>{f.icon}</span>
        <div style={{ minWidth: 0 }}>
          <Text size="sm" fw={700} style={{ lineHeight: 1.15 }}>{f.title}</Text>
          <Text size="xs" c="dimmed">{f.sub}</Text>
        </div>
      </Group>
      <Stack gap={4}>
        {f.rows.map((r) => (
          <Group key={r.label} justify="space-between" wrap="nowrap" gap={6}>
            <Text size="xs" c="dimmed" style={{ minWidth: 0 }}>{r.label}</Text>
            {pill(r.pts, !f.base)}
          </Group>
        ))}
      </Stack>
      <Text size="xs" c="dimmed" ta="right" style={{ marginTop: 'auto', fontStyle: 'italic' }}>{f.foot}</Text>
    </div>
  );
}

function ResultCard({ w, dark }) {
  const g = '#2f9e44';
  return (
    <div style={{ flex: '1 1 150px', minWidth: 150, maxWidth: 200, borderRadius: 10,
      border: `1px solid ${g}`, borderTop: `4px solid ${g}`, padding: '10px 12px',
      background: dark ? 'rgba(47,158,68,0.12)' : '#ebfbee',
      display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}>
      <Text size="xs" c="dimmed">Priority score</Text>
      <Text fw={800} style={{ fontSize: 26, lineHeight: 1, color: g }}>0–{w.scoreMax}</Text>
      <Text size="xs" c="dimmed">Sum, then capped to this range. Highest in practice ≈ {w.derived.practicalMax}.</Text>
    </div>
  );
}

function ScoreWeightsFormula({ w }) {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const factors = [
    { key: 'fill', title: 'In-stock coverage', sub: 'Can we supply it?', color: '#4263eb', icon: <IcBox />, base: true,
      rows: [{ label: 'In stock', pts: w.fill.haveAll }, { label: 'Not in stock', pts: w.fill.haveNone }],
      foot: 'the gate' },
    { key: 'value', title: 'Line value', sub: 'Is it worth chasing?', color: '#f76707', icon: <IcBox />,
      rows: (w.value?.bands || []).slice(0, 3).map((b) => ({ label: `$${Number(b.min).toLocaleString()}+ margin`, pts: b.points })),
      foot: `max +${round1(w.value?.cap ?? 0)} · per line` },
    { key: 'cust', title: 'Customer priority', sub: 'Who is buying?', color: '#7048e8', icon: <IcUser />,
      rows: [{ label: 'Key customer', pts: w.customer.key }, { label: 'Airline / operator', pts: w.customer.operator }, { label: 'Broker / other', pts: 0 }],
      foot: `max +${round1(w.customer.key)}` },
    { key: 'rating', title: 'Account rating', sub: 'Account quality', color: '#0ca678', icon: <IcStar />,
      rows: [{ label: 'A-rated', pts: w.rating.a }, { label: 'B-rated', pts: w.rating.b }, { label: 'Other', pts: 0 }],
      foot: `max +${round1(w.rating.a)}` },
    { key: 'fresh', title: 'RFQ freshness', sub: 'How new is it?', color: '#e8590c', icon: <IcClock />,
      rows: [{ label: 'Brand new', pts: w.freshness.cap }, { label: `~${round1(w.freshness.tauDays)} days old`, pts: round1(w.freshness.cap * Math.exp(-1)) }, { label: 'Week+ old', pts: 0 }],
      foot: 'fades with age' },
  ];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 6, marginTop: 12 }}>
      {factors.map((f, i) => (
        <React.Fragment key={f.key}>
          <FactorCard f={f} dark={dark} />
          <Op>{i < factors.length - 1 ? '+' : '='}</Op>
        </React.Fragment>
      ))}
      <ResultCard w={w} dark={dark} />
    </div>
  );
}

// Small exponential-decay plot of the freshness bonus: cap · e^(−days / tau).
function FreshnessCurve({ w }) {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const cap = Number(w.freshness.cap) || 10;
  const tau = Number(w.freshness.tauDays) || 2;
  const days = Math.max(6, Math.ceil(tau * 4));
  const W = 340, H = 140, padL = 30, padB = 26, padT = 10, padR = 12;
  const px = (t) => padL + (t / days) * (W - padL - padR);
  const py = (v) => (H - padB) - (v / cap) * (H - padB - padT);
  const pts = [];
  for (let t = 0; t <= days; t += days / 60) pts.push(`${px(t).toFixed(1)},${py(cap * Math.exp(-t / tau)).toFixed(1)}`);
  const grid = dark ? '#373a40' : '#e9ecef';
  const axis = dark ? '#5c5f66' : '#ced4da';
  const label = dark ? '#909296' : '#868e96';
  const line = '#e8590c';
  const marks = [0, tau, tau * 2, tau * 3].filter((t) => t <= days);
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: 360, display: 'block' }}
      role="img" aria-label="Freshness bonus decays exponentially with RFQ age">
      {[0, cap / 2, cap].map((v, i) => (
        <g key={i}>
          <line x1={padL} y1={py(v)} x2={W - padR} y2={py(v)} stroke={grid} strokeWidth="1" />
          <text x={padL - 5} y={py(v) + 3} textAnchor="end" fontSize="9" fill={label}>{round1(v)}</text>
        </g>
      ))}
      <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke={axis} strokeWidth="1" />
      <polyline points={pts.join(' ')} fill="none" stroke={line} strokeWidth="2.5" />
      {marks.map((t, i) => {
        const v = cap * Math.exp(-t / tau);
        return (
          <g key={i}>
            <circle cx={px(t)} cy={py(v)} r="3" fill={line} />
            <text x={px(t)} y={H - padB + 13} textAnchor="middle" fontSize="9" fill={label}>{round1(t)}d</text>
            <text x={px(t) + 5} y={py(v) - 5} fontSize="9" fill={line} fontWeight="700">+{round1(v)}</text>
          </g>
        );
      })}
      <text x={padL - 22} y={padT + 4} fontSize="9" fill={label}>pts</text>
      <text x={(padL + W - padR) / 2} y={H - 3} textAnchor="middle" fontSize="9" fill={label}>days since RFQ received</text>
    </svg>
  );
}

/* ─────────────────────────  visual: pricing  ───────────────────────── */

// A numbered scenario row (used for the three pricing situations).
function ScenarioRow({ n, color, title, desc }) {
  return (
    <Paper withBorder radius="md" p="sm" style={{ borderLeft: `4px solid ${color}` }}>
      <Group gap={10} wrap="nowrap" align="flex-start">
        <div style={{ flexShrink: 0, width: 26, height: 26, borderRadius: '50%', background: color,
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
          {n}
        </div>
        <div>
          <Text size="sm" fw={700} style={{ color }}>{title}</Text>
          <Text size="sm" c="dimmed">{desc}</Text>
        </div>
      </Group>
    </Paper>
  );
}

// Descending bar chart of the DOS discount ladder: aged stock gets a bigger discount off SSP.
// Bar length is scaled 0.80→1.00 so the step-down is visible even though multipliers are close.
function DosLadderVisual({ p }) {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const color = '#1098ad';
  const track = dark ? 'var(--mantine-color-dark-5)' : '#e9ecef';
  const bandLabel = (b) => (b.maxDays == null ? `${b.minDays}+ days` : `${b.minDays}–${b.maxDays} days`);
  return (
    <Stack gap={6} mt={12} style={{ maxWidth: 560 }}>
      {p.dosLadder.map((b, i) => {
        const barW = Math.max(6, Math.min(100, ((Number(b.multiplier) - 0.80) / 0.20) * 100));
        return (
          <Group key={i} gap={10} wrap="nowrap" align="center">
            <Text size="xs" c="dimmed" style={{ width: 92, textAlign: 'right', flexShrink: 0 }}>{bandLabel(b)}</Text>
            <div style={{ flex: 1, height: 18, borderRadius: 5, background: track }}>
              <div style={{ height: '100%', width: `${barW}%`, background: color, borderRadius: 5 }} />
            </div>
            <Text size="xs" fw={700} style={{ width: 128, flexShrink: 0, color }}>
              ×{Number(b.multiplier).toFixed(2)} {Number(b.discountPct) > 0 ? `· ${b.discountPct}% off` : '· full price'}
            </Text>
          </Group>
        );
      })}
    </Stack>
  );
}

const BASES = [
  { n: 1, color: '#2f9e44', title: 'Governed SSP × days-on-shelf', desc: 'Our current list price for the condition, discounted for how long the unit has sat. Used only when the price is current.' },
  { n: 2, color: '#1098ad', title: 'Recent comparable sale', desc: 'The most recent real sale of this part (same condition preferred), within the look-back window.' },
  { n: 3, color: '#7048e8', title: 'Repair-implied limit (AR)', desc: 'For as-removed units: a recent serviceable/overhauled sale minus the average repair cost.' },
  { n: 4, color: '#e8590c', title: 'Cost + margin limit', desc: 'Our unit cost marked up to the target margin.' },
  { n: 5, color: '#868e96', title: 'No price data', desc: 'Nothing to anchor on — flagged for the rep and the AI Sales Agent to price by judgment.' },
];

// Vertical "first available wins" waterfall of pricing bases.
function PricingBasisFlow() {
  return (
    <Stack gap={4} mt={10} style={{ maxWidth: 560 }}>
      <Text size="xs" c="dimmed" fw={600}>First available becomes the anchor ↓</Text>
      {BASES.map((b, i) => (
        <React.Fragment key={b.n}>
          <Paper withBorder radius="md" p="xs">
            <Group gap={10} wrap="nowrap" align="flex-start">
              <div style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', border: `2px solid ${b.color}`,
                color: b.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>
                {b.n}
              </div>
              <div>
                <Text size="sm" fw={600}>{b.title}</Text>
                <Text size="xs" c="dimmed">{b.desc}</Text>
              </div>
            </Group>
          </Paper>
          {i < BASES.length - 1 && <Text size="xs" c="dimmed" ta="center" style={{ lineHeight: 1 }}>↓</Text>}
        </React.Fragment>
      ))}
    </Stack>
  );
}


/* ─────────────────────────  visual: summarize  ───────────────────────── */

// Inputs → AI → outputs flow for Summarize.
function SummarizeFlow() {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const box = (bg, border, extra) => ({ borderRadius: 10, border: `1px solid ${border}`, padding: '10px 12px', background: bg, ...extra });
  const arrow = <Text size="lg" c="dimmed" style={{ lineHeight: 1 }}>→</Text>;
  const outChip = (label) => (
    <div key={label} style={{ borderRadius: 8, border: '1px solid #f76707',
      background: dark ? 'rgba(247,103,7,0.15)' : '#fff4e6', padding: '6px 10px' }}>
      <Text size="sm" fw={600}>{label}</Text>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 12 }}>
      <div style={box(dark ? 'var(--mantine-color-dark-6)' : '#e7f5ff', '#4dabf7', { flex: '1 1 150px', minWidth: 150 })}>
        <Text size="xs" c="dimmed" fw={700}>INPUTS</Text>
        <Text size="sm" mt={2}>Context + Score</Text>
      </div>
      {arrow}
      <div style={box(dark ? 'var(--mantine-color-dark-6)' : '#f3f0ff', '#7048e8', { flex: '0 1 160px', minWidth: 140 })}>
        <Text size="xs" c="dimmed" fw={700}>AI SUMMARIZE</Text>
        <Text size="sm" mt={2}>one lightweight pass</Text>
      </div>
      {arrow}
      <Stack gap={6} style={{ flex: '1 1 200px', minWidth: 190 }}>
        {['Customer summary', 'Opportunity summary', 'Flags'].map(outChip)}
      </Stack>
    </div>
  );
}

// Two illustrative example summary cards (sample text) shaped like the cockpit's Customer/Opportunity blocks.
function ExampleSummaries() {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const bg = dark ? 'var(--mantine-color-dark-6)' : '#fff';
  const card = (label, text) => (
    <Paper withBorder radius="md" p="sm" style={{ background: bg, flex: '1 1 260px', minWidth: 240 }}>
      <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ letterSpacing: 0.3 }}>{label}</Text>
      <Text size="sm" mt={4}>{text}</Text>
    </Paper>
  );
  return (
    <Group gap={10} align="stretch" mt={8}>
      {card('Customer', 'Delta Air Lines — key operator, health "Hot", ~$1.2M annual value. Last order 9 days ago; no holds or alerts. Flies 737NG and A320.')}
      {card('Opportunity', 'RFQ for 2× 65-46481-144SWA (SV). AOG — needs a same-day response. We hold 3 SV on shelf. Also open: 2 other quotes for this part, incl. a broker.')}
    </Group>
  );
}

/* ─────────────────────────  visual: judge  ───────────────────────── */

// base score  +  bounded judge nudge  =  final priority
function JudgeNudge({ w }) {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const chip = (bg, border, top, main, sub) => (
    <div style={{ borderRadius: 10, border: `1px solid ${border}`, borderTop: `4px solid ${border}`,
      padding: '10px 12px', background: bg, flex: '1 1 150px', minWidth: 140 }}>
      <Text size="xs" c="dimmed" fw={700}>{top}</Text>
      <Text fw={800} style={{ fontSize: 18, lineHeight: 1.15 }}>{main}</Text>
      <Text size="xs" c="dimmed" mt={2}>{sub}</Text>
    </div>
  );
  const op = (t) => <Text size="lg" c="dimmed" style={{ lineHeight: 1 }}>{t}</Text>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 12 }}>
      {chip(dark ? 'var(--mantine-color-dark-6)' : '#f1f3f5', '#868e96', 'BASE SCORE', 'Score', 'deterministic')}
      {op('+')}
      {chip(dark ? 'var(--mantine-color-dark-6)' : '#f3f0ff', '#7048e8', 'JUDGE NUDGE', `−${w.nudgeCap} … +${w.nudgeCap}`, 'cited guidance')}
      {op('=')}
      {chip(dark ? 'rgba(47,158,68,0.12)' : '#ebfbee', '#2f9e44', 'FINAL PRIORITY', `0–${w.scoreMax}`, 'what the rep sees')}
    </div>
  );
}

const JUDGE_ACTIONS = [
  { label: 'Quote now', color: '#2f9e44', desc: 'Fillable, good customer, nothing to check.' },
  { label: 'Quote with review', color: '#1098ad', desc: 'Fillable, but verify something first (hold, thin margin, pricing).' },
  { label: 'Hold for sourcing', color: '#e8590c', desc: 'Not in stock, but worth sourcing.' },
  { label: 'Request more info', color: '#f08c00', desc: 'RFQ unclear — missing condition or ambiguous part number.' },
  { label: 'Deprioritize', color: '#868e96', desc: 'Low value, no stock, do-not-quote, or already handled.' },
];
function ActionCards() {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const bg = dark ? 'var(--mantine-color-dark-6)' : '#fff';
  return (
    <Stack gap={6} mt={10} style={{ maxWidth: 640 }}>
      {JUDGE_ACTIONS.map((a) => (
        <Paper key={a.label} withBorder radius="md" p="xs" style={{ background: bg, borderLeft: `4px solid ${a.color}` }}>
          <Group gap={10} wrap="nowrap" align="baseline">
            <Text size="sm" fw={700} style={{ color: a.color, width: 132, flexShrink: 0 }}>{a.label}</Text>
            <Text size="sm" c="dimmed">{a.desc}</Text>
          </Group>
        </Paper>
      ))}
    </Stack>
  );
}

/* ─────────────────────────  visual: nymeria  ───────────────────────── */

// The whole board + guidance + Vesper's never-below → Nymeria (agent) → one decision.
function NymeriaFlow() {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const box = (bg, border, extra) => ({ borderRadius: 10, border: `1px solid ${border}`, padding: '10px 12px', background: bg, ...extra });
  const arrow = <Text size="lg" c="dimmed" style={{ lineHeight: 1 }}>→</Text>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 12 }}>
      <div style={box(dark ? 'var(--mantine-color-dark-6)' : '#e7f5ff', '#4dabf7', { flex: '1 1 190px', minWidth: 180 })}>
        <Text size="xs" c="dimmed" fw={700}>THE WHOLE BOARD</Text>
        <Text size="sm" mt={2}>customer · stock · history · demand · guidance · the pipeline's never-below</Text>
      </div>
      {arrow}
      <div style={box(dark ? 'var(--mantine-color-dark-6)' : '#f8f0fc', '#ae3ec9', { flex: '0 1 150px', minWidth: 130 })}>
        <Text size="xs" c="dimmed" fw={700}>AI SALES AGENT</Text>
        <Text size="sm" mt={2}>the sales agent decides</Text>
      </div>
      {arrow}
      <div style={box(dark ? 'rgba(174,62,201,0.14)' : '#f3e8f7', '#ae3ec9', { flex: '1 1 160px', minWidth: 150 })}>
        <Text size="xs" c="dimmed" fw={700}>THE DECISION</Text>
        <Text size="sm" mt={2}>quote · hold · pass</Text>
      </div>
    </div>
  );
}

const NYMERIA_OUTCOMES = [
  { label: 'Quote', color: '#2f9e44', desc: 'Name the concrete line — part, condition, quantity, and a price at or above the never-below.' },
  { label: 'Hold', color: '#e8590c', desc: 'Worth pursuing, but verify something first (credit, sourcing, pricing).' },
  { label: 'Pass', color: '#868e96', desc: 'Not a fit — e.g. we only hold AR and the buyer is an operator, or a do-not-quote rule applies.' },
];
function OutcomeCards() {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const bg = dark ? 'var(--mantine-color-dark-6)' : '#fff';
  return (
    <Group gap={8} mt={10} align="stretch">
      {NYMERIA_OUTCOMES.map((o) => (
        <div key={o.label} style={{ flex: '1 1 170px', minWidth: 160, maxWidth: 240, borderRadius: 10,
          border: `1px solid ${o.color}`, borderTop: `4px solid ${o.color}`, padding: '10px 12px', background: bg }}>
          <Text fw={800} style={{ fontSize: 16, color: o.color }}>{o.label}</Text>
          <Text size="xs" c="dimmed" mt={4}>{o.desc}</Text>
        </div>
      ))}
    </Group>
  );
}
